import React, { useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { useTenantExportBranding, buildBrandedHeaderHTML } from '../../hooks/useTenantExportBranding';

/**
 * Shared branding header component for React-based views (ReportCanvas, DashboardGrid).
 * Matches the design of buildBrandedHeaderHTML.
 */
export const ExportBrandingHeader = ({ title, meta, sx = {}, children }) => {
    const { logoUrl, portalName, loading } = useTenantExportBranding();
    const [logoBase64, setLogoBase64] = useState(null);
    const [imageLoaded, setImageLoaded] = useState(false);

    useEffect(() => {
        if (logoUrl) {
            // If already base64, skip
            if (logoUrl.startsWith('data:')) {
                setLogoBase64(logoUrl);
                setImageLoaded(true);
                return;
            }

            // Convert to base64 to ensure html2canvas can capture it without CORS issues
            fetch(logoUrl)
                .then(r => r.blob())
                .then(blob => {
                    const reader = new FileReader();
                    reader.onloadend = () => {
                        setLogoBase64(reader.result);
                        setImageLoaded(true);
                    };
                    reader.readAsDataURL(blob);
                })
                .catch(err => {
                    console.warn('[ExportBrandingHeader] Base64 conversion failed:', err);
                    setLogoBase64(logoUrl); // Fallback to original URL
                    setImageLoaded(true);
                });
        } else {
            setLogoBase64(null);
            setImageLoaded(true);
        }
    }, [logoUrl]);

    if (loading) return null;

    return (
        <Box 
            id="export-branding-header"
            className="export-branding-header"
            sx={{ 
                width: '100%', 
                textAlign: 'center',
                pb: 2.5,
                mb: 4,
                borderBottom: '1px solid #e2e8f0',
                ...sx 
            }}
        >
            {logoUrl ? (
                <Box sx={{ mb: 2, display: 'flex', justifyContent: 'center' }}>
                    <img 
                        src={logoBase64 || logoUrl} 
                        alt="" 
                        style={{ maxHeight: 74, maxWidth: 320, objectFit: 'contain', display: 'block' }}
                    />
                </Box>
            ) : portalName ? (
                <Typography sx={{ fontSize: 18, fontWeight: 700, color: 'primary.main', mb: 1.5 }}>
                    {portalName}
                </Typography>
            ) : null}

            {children ? children : (title ? (
                <>
                    <Typography variant="h4" sx={{ fontSize: 28, fontWeight: 800, color: '#0f172a', m: 0, lineHeight: 1.2 }}>
                        {title}
                    </Typography>
                    {meta && (
                        <Typography sx={{ fontSize: 12, color: '#94a3b8', mt: 1 }}>
                            {meta}
                        </Typography>
                    )}
                </>
            ) : null)}
        </Box>
    );
};

/**
 * Helper to ensure images in the export container are loaded before capture.
 * @param {HTMLElement} container 
 * @returns {Promise<void>}
 */
export const waitForBrandingImages = async (container) => {
    const images = Array.from(container.querySelectorAll('img'));
    const promises = images.map(img => {
        if (img.complete) return Promise.resolve();
        return new Promise((resolve) => {
            img.onload = resolve;
            img.onerror = resolve;
        });
    });
    // Also wait a small buffer for layout stability
    await Promise.all(promises);
    await new Promise(r => setTimeout(r, 500));
};

export default ExportBrandingHeader;
